import { Extension } from '@tiptap/core';
import { Table } from '@tiptap/extension-table';
import { Fragment } from '@tiptap/pm/model';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';

/**
 * 표 마크다운 직렬화 교체 (`AxRichTextEditor` 전용).
 * 설계: `docs/designs/rich-text-editing-quality-and-block-features.md` §5 F0
 *
 * `tiptap-markdown@0.9.0` 의 기본 표 직렬화(src/extensions/nodes/table.js)는 두 가지로
 * **본문을 조용히 파괴한다**.
 *
 * 1. 셀 안 리터럴 파이프(`|`)를 이스케이프하지 않는다. `prosemirror-markdown` 의
 *    `esc()` 는 `` ` * \ ~ [ ] _ `` 만 escape 하므로 셀에 `|` 를 적으면 저장 시 열이
 *    밀리고 **옆칸 내용이 영구 소실**된다.
 * 2. 마크다운으로 표현 못 하는 구조(다문단 셀·헤더 행 없음·병합 셀)를 만나면
 *    `isMarkdownSerializable` 이 false 를 돌려주고 HTMLNode 폴백으로 넘어가
 *    `html: false` 인 우리 설정에서는 **표 전체가 `[table]` 다섯 글자로 저장**된다.
 *    화면의 표는 그대로라 사용자는 알아채지 못한다.
 *
 * 그래서 직렬화를 통째로 우리가 맡는다. 원칙은 **어떤 문서 상태에서도 내용을 버리지
 * 않는다**이다 — 표현 못 하는 구조는 아래처럼 한 단계 낮춰서라도 전부 적는다.
 *
 * - 헤더 행이 없으면 첫 행을 헤더 자리에 쓴다(GFM 표는 헤더가 필수).
 * - 셀 안 문단이 여러 개면 공백으로 이어 한 줄로 만든다.
 * - 셀 안 `hardBreak`(Shift+Enter)는 공백으로 바꾼다. 기본 구현은 표 안에서
 *   `[hardBreak]` 를 적는다.
 * - 병합 셀(colspan/rowspan)은 colspan 만큼 빈 셀을 채워 열 수를 맞춘다.
 *
 * 파이프는 `prosemirror-markdown` 의 `escapeExtraCharacters` 옵션을 셀 렌더 동안만
 * 켜서 `\|` 로 적는다. `markdown-it` 의 표 규칙(`escapedSplit`)이 `\|` 를 열 구분이
 * 아닌 리터럴로 되돌리므로 왕복이 멱등해진다.
 *
 * `html: false → true` 전환은 해법이 아니다 — 본문에 글자로 적힌 태그와 HR 문서의
 * `<!-- PARTY_TABLE -->` 마커까지 해석되어 사라진다(`text-color.extension.ts` 참고).
 */

/**
 * 직렬화 폴백이 남긴 흔적. 이 문자열이 본문에 나타나면 그 저장본은 이미 손상된
 * 것이므로 `AxRichTextEditor` 가 emit 을 보류하고 직전 정상값을 유지한다.
 */
export const UNSERIALIZABLE_MARKDOWN_NODE_RE =
  /\[(table|tableRow|tableCell|tableHeader|hardBreak)\]/;

/** 셀 렌더 동안만 켜는 추가 escape 대상 — 표에서 열 구분자로 읽히는 파이프. */
const TABLE_CELL_ESCAPE = /\|/g;

interface TableSerializerState {
  inTable: boolean;
  // 원래 값을 그대로 되돌려 놓아야 하므로 undefined 도 담을 수 있어야 한다
  // (exactOptionalPropertyTypes 아래에서는 `?:` 만으로는 undefined 대입이 막힌다).
  options: { escapeExtraCharacters?: RegExp | null | undefined };
  write: (content?: string) => void;
  ensureNewLine: () => void;
  closeBlock: (node: ProseMirrorNode) => void;
  renderInline: (parent: ProseMirrorNode) => void;
}

/** 자식 노드 목록. 상류가 쓰는 `content.content` 내부 접근 대신 공개 API 로 훑는다. */
function childNodesOf(node: ProseMirrorNode | undefined): ProseMirrorNode[] {
  if (!node) return [];
  const children: ProseMirrorNode[] = [];
  node.forEach((child) => children.push(child));
  return children;
}

/** 병합 셀은 차지하는 열 수만큼 자리를 쓴다. 값이 없거나 이상하면 1. */
function columnSpanOf(cell: ProseMirrorNode): number {
  const raw: unknown = cell.attrs.colspan;
  return typeof raw === 'number' && Number.isFinite(raw) && raw > 1 ? Math.floor(raw) : 1;
}

/** 행이 차지하는 열 수(병합 포함). */
function rowWidth(row: ProseMirrorNode): number {
  return childNodesOf(row).reduce((sum, cell) => sum + columnSpanOf(cell), 0);
}

/**
 * 셀 하나를 **한 줄 인라인**으로 평탄화한다.
 *
 * 문단이 여러 개면 공백으로 잇고, `hardBreak` 는 공백으로 바꾼다. 목록·이미지처럼
 * 인라인이 아닌 블록이 들어 있으면 텍스트만 뽑아 넣는다(구조는 잃어도 글자는 남긴다).
 * 내용이 전부 공백이면 null 을 돌려 빈 셀로 적게 한다.
 */
function flattenCellContent(cell: ProseMirrorNode): ProseMirrorNode | null {
  if (!cell.textContent.trim() && cell.childCount <= 1) return null;

  const { schema } = cell.type;
  const parts: ProseMirrorNode[] = [];

  childNodesOf(cell).forEach((block) => {
    const pieces: ProseMirrorNode[] = [];
    if (block.isTextblock) {
      block.content.forEach((child) => {
        pieces.push(child.type.name === 'hardBreak' ? schema.text(' ') : child);
      });
    } else if (block.textContent) {
      pieces.push(schema.text(block.textContent));
    }
    if (pieces.length === 0) return;
    if (parts.length > 0) parts.push(schema.text(' '));
    parts.push(...pieces);
  });

  if (parts.length === 0) return null;
  const fragment = Fragment.fromArray(parts);
  if (fragment.size === 0) return null;

  // renderInline 은 자식만 순회하므로 담을 그릇만 있으면 된다(스키마 검증 없음).
  const holder = schema.nodes.paragraph ?? cell.firstChild?.type;
  if (!holder) return null;
  return holder.create(null, fragment);
}

function writeCell(state: TableSerializerState, cell: ProseMirrorNode): void {
  const content = flattenCellContent(cell);
  if (!content) return;

  const previousEscape = state.options.escapeExtraCharacters;
  state.options.escapeExtraCharacters = TABLE_CELL_ESCAPE;
  try {
    state.renderInline(content);
  } finally {
    state.options.escapeExtraCharacters = previousEscape;
  }
}

function writeRow(state: TableSerializerState, row: ProseMirrorNode, columnCount: number): void {
  state.write('| ');
  let written = 0;

  childNodesOf(row).forEach((cell) => {
    const span = columnSpanOf(cell);
    for (let offset = 0; offset < span && written < columnCount; offset += 1) {
      if (written > 0) state.write(' | ');
      // 병합으로 늘어난 자리는 빈 칸으로 채운다 — 내용은 첫 칸에만 적는다.
      if (offset === 0) writeCell(state, cell);
      written += 1;
    }
  });

  // rowspan 으로 셀이 비는 행, 행마다 열 수가 다른 표 → 남는 자리를 빈 칸으로 맞춘다.
  for (; written < columnCount; written += 1) {
    if (written > 0) state.write(' | ');
  }

  state.write(' |');
  state.ensureNewLine();
}

function serializeTable(state: TableSerializerState, node: ProseMirrorNode): void {
  const rows = childNodesOf(node);
  const columnCount = rows.reduce((max, row) => Math.max(max, rowWidth(row)), 0);

  if (rows.length === 0 || columnCount === 0) {
    state.closeBlock(node);
    return;
  }

  state.inTable = true;
  rows.forEach((row, rowIndex) => {
    writeRow(state, row, columnCount);
    if (rowIndex === 0) {
      // 헤더 행이 없는 표라도 첫 행 뒤에 구분행을 넣어 GFM 표로 성립시킨다.
      const delimiter = Array.from({ length: columnCount }, () => '---').join(' | ');
      state.write(`| ${delimiter} |`);
      state.ensureNewLine();
    }
  });
  state.closeBlock(node);
  state.inTable = false;
}

/**
 * 기본 Table 확장에 우리 마크다운 직렬화만 얹은 것. 스키마·명령·열 너비 조절 동작은
 * 그대로다(`tiptap-markdown` 의 `getMarkdownSpec` 은 확장 자신의 storage 를 기본 스펙
 * 보다 우선한다).
 */
export const AxMarkdownTable = Table.extend({
  addStorage() {
    const parent = this.parent?.() as Record<string, unknown> | undefined;
    return {
      ...parent,
      markdown: {
        serialize: serializeTable,
        parse: {
          // markdown-it 의 표 규칙이 처리한다.
        },
      },
    };
  },
});

const CELL_TYPE_NAMES = new Set(['tableCell', 'tableHeader']);

/**
 * 표 셀 안에서 Enter/Shift+Enter 를 **다음 셀 이동**으로 바꾼다.
 *
 * 마크다운 표의 셀은 한 줄이다 — 셀 안에 문단을 하나 더 만들거나 줄바꿈을 넣어도
 * 저장했다 열면 공백 하나로 합쳐진다(위 직렬화가 내용을 버리지 않으려고 그렇게 한다).
 * 만들 수 없는 것을 만들게 두었다가 조용히 바꾸느니, 애초에 유용한 동작으로 돌린다.
 * 마지막 셀이면 행을 하나 추가하고 그 첫 칸으로 간다.
 */
export const AxTableCellEnterGuard = Extension.create({
  name: 'axTableCellEnterGuard',
  // StarterKit 의 Enter(문단 분할·hardBreak, 우선순위 100)보다 먼저 평가되어야 한다.
  priority: 101,

  addKeyboardShortcuts() {
    const moveToNextCell = (): boolean => {
      const { editor } = this;
      if (!editor.isEditable) return false;
      const { $from } = editor.state.selection;
      const inCell = Array.from({ length: $from.depth }, (_, i) => $from.node(i + 1)).some((node) =>
        CELL_TYPE_NAMES.has(node.type.name),
      );
      if (!inCell) return false;

      if (editor.commands.goToNextCell()) return true;
      // 표의 마지막 셀 — 행을 늘려 이어서 입력하게 한다.
      return editor.chain().addRowAfter().goToNextCell().run();
    };

    return {
      Enter: moveToNextCell,
      'Shift-Enter': moveToNextCell,
    };
  },
});
